import React from "react";
import { X } from "@phosphor-icons/react";
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useSettings } from "@/context/SettingsContext";
import { pick } from "@/lib/languages";
import { cn } from "@/lib/utils";
import { appShellL } from "@/i18n/appShell";

// A sheet's scrolling body and its footer, for a sheet whose content draws
// both itself (`bare`): the content then mounts at each opening with its own
// state, the dates sheet's draft for one.
export const APP_SHEET_BODY = "min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-4";
export const APP_SHEET_FOOTER = "flex items-center gap-3 border-t border-touki-border-soft px-4 py-3";

/**
 * A bottom sheet the apps open where the website has a popover or none (the
 * dates, the language and currency). It is the site's Radix sheet, which
 * traps focus and closes on the scrim, Escape and the Android back button
 * (NativeBridge), drawn as the approved design draws sheets: 24 px top
 * corners, the grab handle, a title row of 18/800 with its 44 px close
 * button, a body that scrolls and a footer for the action above the gesture
 * bar.
 */
export default function AppSheet({ open, onOpenChange, title, description, footer, children, className, bodyClassName, bare = false, "data-testid": testId }) {
  const { lang } = useSettings();
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      {/* A sheet without a description says so to Radix (aria-describedby left undefined). */}
      <SheetContent side="bottom" hideClose data-testid={testId} {...(description ? {} : { "aria-describedby": undefined })}
        className={cn("tk-app-sheet flex max-h-[86dvh] flex-col gap-0 p-0", className)}>
        <div className="flex items-center gap-2 pb-2 pe-2 ps-4 pt-1">
          <SheetTitle className="min-w-0 flex-1 text-heading-md font-black text-touki-black">{title}</SheetTitle>
          <SheetClose className="tk-icon-btn" data-testid={testId ? `${testId}-close` : undefined}>
            <X size={24} aria-hidden="true" />
            <span className="sr-only">{pick(appShellL, lang).sheets.close}</span>
          </SheetClose>
        </div>
        {description ? <SheetDescription className="sr-only">{description}</SheetDescription> : null}
        {bare ? children : <div className={cn(APP_SHEET_BODY, bodyClassName)}>{children}</div>}
        {footer ? <div className={APP_SHEET_FOOTER}>{footer}</div> : null}
      </SheetContent>
    </Sheet>
  );
}
